
import React from 'react';
import { SimulationConfig, BallFillType, BallTexture } from './types';
import { SectionLabel } from './SectionLabel';
import { SmartInput } from './SmartInput';
import { Toggle } from './Toggle';
import { ControlGroup } from './ControlGroup';

interface SphereDisplaySettingsProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const SphereDisplaySettings: React.FC<SphereDisplaySettingsProps> = ({ config, setConfig }) => {
  const fillTypes: BallFillType[] = ['SOLID', 'GRADIENT_RADIAL', 'GRADIENT_LINEAR', 'HOLLOW'];
  const textures: BallTexture[] = ['NONE', 'STRIPES', 'DOTS', 'CROSSHATCH', 'NOISE'];

  const ColorPicker = ({ label, value, onChange }: { label: string, value: string, onChange: (v: string) => void }) => (
    <ControlGroup label={label}>
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-full border border-white/20 shadow-sm" style={{ backgroundColor: value }} />
        <div className="relative flex-1">
             <input type="color" value={value} onChange={e => onChange(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
             <input type="text" value={value} onChange={e => onChange(e.target.value)} className={`w-full px-2 py-1 text-[9px] font-mono border rounded-lg ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`} />
        </div>
      </div>
    </ControlGroup>
  );

  return (
    <div className="flex flex-col gap-4">
      <SectionLabel icon="fa-circle-notch" darkMode={config.darkMode}>Sphere Studio Engine</SectionLabel>

      {/* GEOMETRY & PHYSICS VIS */}
      <div className="p-4 rounded-3xl border bg-blue-500/5 border-blue-500/10 flex flex-col gap-3">
         <span className="text-[10px] font-black uppercase text-blue-400 opacity-80">Geometry & Physics FX</span>
         <SmartInput label="Radius" min={1} max={150} value={config.ballRadius} onChange={v => setConfig(p => ({...p, ballRadius: v}))} darkMode={config.darkMode} />
         
         <div className="grid grid-cols-2 gap-3">
             <div className="flex items-center justify-between border border-blue-500/10 p-2 rounded-xl bg-blue-500/5">
                 <span className="text-[9px] font-black uppercase opacity-60">Squash & Stretch</span>
                 <Toggle active={config.ballSquashStretch} onClick={() => setConfig(p => ({...p, ballSquashStretch: !p.ballSquashStretch}))} color="bg-blue-500" />
             </div>
             <SmartInput label="Squash Intensity" min={0.1} max={2.0} step={0.1} value={config.ballSquashFactor} onChange={v => setConfig(p => ({...p, ballSquashFactor: v}))} darkMode={config.darkMode} />
         </div>
         <SmartInput label="Pulse Speed" min={0} max={20} value={config.ballPulseSpeed} onChange={v => setConfig(p => ({...p, ballPulseSpeed: v}))} darkMode={config.darkMode} />
      </div>

      {/* FILL & SURFACE */}
      <div className="p-4 rounded-3xl border bg-purple-500/5 border-purple-500/10 flex flex-col gap-3">
         <span className="text-[10px] font-black uppercase text-purple-400 opacity-80">Surface Material</span>
         
         <ControlGroup label="Fill Mode">
            <select value={config.ballFillType} onChange={e => setConfig(p => ({...p, ballFillType: e.target.value as BallFillType}))} className={`w-full px-2 py-1.5 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}>
                {fillTypes.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
         </ControlGroup>

         <div className="grid grid-cols-2 gap-3">
             <ColorPicker label="Base Color" value={config.ballColor} onChange={v => setConfig(p => ({...p, ballColor: v}))} />
             <SmartInput label="Opacity" min={0} max={1} step={0.05} value={config.ballFillOpacity} onChange={v => setConfig(p => ({...p, ballFillOpacity: v}))} darkMode={config.darkMode} />
         </div>

         {(config.ballFillType === 'GRADIENT_RADIAL' || config.ballFillType === 'GRADIENT_LINEAR') && (
             <div className="grid grid-cols-2 gap-3 p-2 bg-black/5 rounded-xl border border-white/5">
                 <ColorPicker label="Gradient Inner" value={config.ballGradientInner} onChange={v => setConfig(p => ({...p, ballGradientInner: v}))} />
                 <ColorPicker label="Gradient Outer" value={config.ballGradientOuter} onChange={v => setConfig(p => ({...p, ballGradientOuter: v}))} />
             </div>
         )}

         <ControlGroup label="Texture Overlay">
            <select value={config.ballTexture} onChange={e => setConfig(p => ({...p, ballTexture: e.target.value as BallTexture}))} className={`w-full px-2 py-1.5 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}>
                {textures.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
         </ControlGroup>
         {config.ballTexture !== 'NONE' && (
             <SmartInput label="Texture Opacity" min={0} max={1} step={0.05} value={config.ballTextureOpacity} onChange={v => setConfig(p => ({...p, ballTextureOpacity: v}))} darkMode={config.darkMode} />
         )}
      </div>

      {/* BORDER & OUTLINE */}
      <div className="p-4 rounded-3xl border bg-emerald-500/5 border-emerald-500/10 flex flex-col gap-3">
         <span className="text-[10px] font-black uppercase text-emerald-400 opacity-80">Border Engine</span>
         <div className="grid grid-cols-2 gap-3">
             <SmartInput label="Width" min={0} max={20} value={config.ballStrokeWidth} onChange={v => setConfig(p => ({...p, ballStrokeWidth: v}))} darkMode={config.darkMode} />
             <SmartInput label="Opacity" min={0} max={1} step={0.05} value={config.ballStrokeOpacity} onChange={v => setConfig(p => ({...p, ballStrokeOpacity: v}))} darkMode={config.darkMode} />
         </div>
         <ColorPicker label="Border Color" value={config.ballStrokeColor} onChange={v => setConfig(p => ({...p, ballStrokeColor: v}))} />
      </div>

      {/* GLOW & FX */}
      <div className="p-4 rounded-3xl border bg-orange-500/5 border-orange-500/10 flex flex-col gap-3">
         <span className="text-[10px] font-black uppercase text-orange-400 opacity-80">Glow & Radiance</span>
         <div className="grid grid-cols-2 gap-3">
             <SmartInput label="Glow Radius" min={0} max={100} value={config.ballGlowRadius} onChange={v => setConfig(p => ({...p, ballGlowRadius: v}))} darkMode={config.darkMode} />
             <SmartInput label="Intensity" min={0} max={1} step={0.05} value={config.ballGlowOpacity} onChange={v => setConfig(p => ({...p, ballGlowOpacity: v}))} darkMode={config.darkMode} />
         </div>
         <ColorPicker label="Glow Color" value={config.ballGlowColor} onChange={v => setConfig(p => ({...p, ballGlowColor: v}))} />
      </div>
      
      {/* MULTI-BALL COLORS (Legacy/Compat support) */}
      <div className="p-4 rounded-3xl border bg-neutral-500/5 border-neutral-500/10 flex flex-col gap-3">
        <span className="text-[10px] font-black uppercase opacity-50">Multi-Ball Overrides</span>
        <div className="grid grid-cols-2 gap-3">
            <ColorPicker label="Primary Sphere" value={config.ball1Color} onChange={v => setConfig(p => ({...p, ball1Color: v}))} />
            <ColorPicker label="Secondary Sphere" value={config.ball2Color} onChange={v => setConfig(p => ({...p, ball2Color: v}))} />
        </div>
      </div>
    </div>
  );
};
